import React, { ChangeEvent, useCallback, useMemo } from "react";
import styled from "@emotion/styled/macro";
import {
  Select,
  tokens,
  Typography,
  LoadingIndicator,
} from "@sunrun/experience-ui-components";
import { useParams } from "react-router-dom";
import { useFlags } from "flagsmith/react";
import { useMediaQuery } from "react-responsive";
import FranklinBattery from "../../../assets/images/franklin-img.svg";
import TeslaBattery from "../../../assets/images/tesla-img.svg";
import EvCharger from "../../../assets/images/tesla-ev-charger-img.svg";
import { CheckoutHeader } from "../molecules/CheckoutHeader";
import {
  CheckoutFullWidthContentGrid,
  CheckoutGrid,
  CheckoutHeaderGrid,
} from "../templates/CheckoutGrid";
import { CheckoutFooter } from "../molecules/CheckoutFooter";
import { CheckoutStep } from "../molecules/CheckoutStep";
import { CheckoutOfferImage } from "../molecules/CheckoutOfferImage";
import { ContactStatusNotification } from "../atoms/ContactStatusNotification";
import { Mixpanel } from "../../../providers/mixpanel";
import { NonSolarDisplay } from "../../../components/atoms/NonSolarDisplay";
import { ListOffer } from "../../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { OPTY_PROGRESS_STEPS } from "checkout/util/constants";
import {
  useNavigateToCheckout,
  useNavigateToNoOffer,
} from "utils/useNavigateHelper";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { maxWidth, mobileMaxWidth } from "components/atoms/GlobalStyles";
import { getOfferToShow } from "utils/offerListHelpers";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useOfferList } from "checkout/hooks/useCheckoutSwr";
import { FeatureFlags } from "fixtures/features";
import { useAuthToken } from "hooks/useAuthToken";
import { CUSTOMER_SELF_CHECKOUT__CLICKED_CONTINUE_CTA_CONFIRM_OFFER_PAGE } from "constants/mixpanelEvents";
import { CASH } from "constants/financialProducts";
import bgNewHomes from "components/assets/images/bg-new-homes.jpg";
import { BUILDER_OFFER_CHECKOUT_MESSAGE } from "constants/newHomes";
import { excludeBuilderOffers, isBuilderOffer } from "utils/builderOffer";

const CheckoutConfirmOfferPage: React.FC = () => {
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const isCustomer = !!proposalToken;
  const navigateToCheckout = useNavigateToCheckout();
  const navigateToNoOffer = useNavigateToNoOffer();
  const { prospectId, offerId } = useParams();
  const cognitoToken = useAuthToken("IHD")?.token;
  const {
    offer,
    isOfferLoading,
    proposalId,
    isOnlyBattery,
    isOnlyEvCharger,
    isNewHomes,
  } = useCheckoutContext();

  const flags = useFlags([
    FeatureFlags.STANDALONE_EV_CHARGER,
    FeatureFlags.CHECKOUT_SRA,
  ]);
  const isCheckoutSraEnabled =
    flags[FeatureFlags.CHECKOUT_SRA]?.enabled ?? false;

  const standaloneEvChargerFeatureFlag =
    flags[FeatureFlags.STANDALONE_EV_CHARGER]?.enabled;

  const isBattery = isOnlyBattery;
  const isEvCharger = standaloneEvChargerFeatureFlag && isOnlyEvCharger;
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  if (!prospectId) {
    throw Error("Must have a prospect id");
  }

  const { offerList, isOfferListLoading, offerListError } = useOfferList(
    prospectId,
    cognitoToken,
    proposalToken
  );

  const selectableOffers = useMemo(() => {
    if (!offerList) {
      return [];
    }

    if (isCustomer) {
      return excludeBuilderOffers(offerList);
    }

    return offerList;
  }, [offerList, isCustomer]);

  if (!offerId && offerList) {
    if (selectableOffers.length) {
      const offerToShow = getOfferToShow(selectableOffers);
      navigateToCheckout("confirm-offer", offerToShow.id);
    } else {
      navigateToNoOffer();
    }
  }

  const offerOptions = useMemo(() => {
    if (!selectableOffers.length) {
      return [];
    }

    let resultList: ListOffer[] = selectableOffers;

    if (isNewHomes && isCustomer) {
      resultList = selectableOffers.filter((e) => e.financing !== CASH);
    }

    return resultList.map((e) => {
      return { label: e.label, value: e.id };
    });
  }, [selectableOffers, isNewHomes, isCustomer]);

  const isBuilderOfferSelected = Boolean(offer && isBuilderOffer(offer));

  const onOfferSelect = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      navigateToCheckout("confirm-offer", event.target.value);
    },
    [navigateToCheckout]
  );

  const navigateToFirstCheckoutTask = useCallback(() => {
    if (!offer) {
      throw Error("Cannot start checkout if offer does not exist.");
    }
    if (isCustomer) {
      Mixpanel.track(
        CUSTOMER_SELF_CHECKOUT__CLICKED_CONTINUE_CTA_CONFIRM_OFFER_PAGE,
        {
          prospectId,
          offerId: offer?.id,
          proposalId,
        }
      );
    }
    const firstTaskSlug =
      !isCustomer && isCheckoutSraEnabled
        ? "confirm-home-info"
        : "confirm-names";
    navigateToCheckout(firstTaskSlug, offer.id);
  }, [
    isCheckoutSraEnabled,
    isCustomer,
    navigateToCheckout,
    offer,
    prospectId,
    proposalId,
  ]);

  if (isOfferLoading || isOfferListLoading || !offer) {
    return (
      <LoadingIndicator color="black" fullScreen={true}></LoadingIndicator>
    );
  }
  const isTeslaBattery = offer?.batteryManufacturer === "Tesla";
  const OfferImage = ({
    isBattery,
    isEvCharger,
  }: {
    isBattery: boolean;
    isEvCharger: boolean;
  }) => {
    if (isBattery) {
      return (
        <StyledNonSolarDisplay
          className="Checkout-Confirm-Offer-Battery-Image"
          dataTestId={
            `Non-Solar-Battery-Display-Container-` +
            (isTeslaBattery ? "TeslaBattery" : "FranklinBattery")
          }
          src={isTeslaBattery ? TeslaBattery : FranklinBattery}
          alt={isTeslaBattery ? "Tesla Battery" : "Franklin Battery"}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      );
    } else if (isEvCharger) {
      return (
        <StyledNonSolarDisplay
          className="Checkout-Confirm-Offer-Ev-Charger-Image"
          dataTestId="Non-Solar-Ev-Charger-Display-Container"
          src={EvCharger}
          alt="Ev Charger"
          isMobile={isMobile}
          isTablet={isTablet}
        />
      );
    } else {
      return (
        <CheckoutOfferImage
          designId={offer?.designId}
          designVersion={offer?.designVersion}
        />
      );
    }
  };

  return (
    <StyledCheckoutGrid showMobilePadding={isMobile}>
      <CheckoutHeaderGrid>
        {isMobile && <StyledMobileHeaderNote>Checkout</StyledMobileHeaderNote>}
        <CheckoutHeader headerTitle="Confirm Your Offer" />
      </CheckoutHeaderGrid>
      <CheckoutFullWidthContentGrid isMobile={isMobile}>
        {offer.isExpired && (
          <ContactStatusNotification
            isMobile={isMobile}
            status="Error"
            message="This offer is expired. You won't be able to checkout with this offer."
          />
        )}
        {isBuilderOfferSelected && (
          <ContactStatusNotification
            isMobile={isMobile}
            status="Error"
            message={BUILDER_OFFER_CHECKOUT_MESSAGE}
          />
        )}
        <ConfirmOfferPageContentContainer isMobile={isMobile}>
          {offerListError && (
            <ErrorComponent
              context="ReviewPage"
              action="Get Offer List"
              error={offerListError}
            />
          )}
          {!offerListError && (
            <>
              <ConfirmOfferPageSection>
                <OfferSelect>
                  <Typography
                    variant="p"
                    size={tokens.FONT_SIZE_3}
                    fontWeight="light"
                    lineHeight={tokens.LINE_HEIGHTS_12}
                    style={{ marginBottom: 10 }}
                  >
                    You've selected:
                  </Typography>
                  {!isOfferLoading &&
                    !isOfferListLoading &&
                    offerOptions &&
                    offerOptions.length > 0 && (
                      <>
                        <Select
                          value={offer!.id}
                          options={offerOptions}
                          onChange={onOfferSelect}
                          role="select"
                        />
                        {isNewHomes ? (
                          <ConfirmOfferNewHomesDesignWrapper>
                            <NewHomesDisclaimer
                              size={12}
                              color={tokens.BRAND_MEDIUM_BLUE}
                            >
                              This is a representative image and not your actual
                              home
                            </NewHomesDisclaimer>
                            <ConfirmOfferNewHomesDesignImage
                              bgImageUrl={bgNewHomes}
                              data-testid="checkout-confirm-offer-new-homes-design-image"
                            />
                          </ConfirmOfferNewHomesDesignWrapper>
                        ) : (
                          <OfferImage
                            isBattery={isBattery}
                            isEvCharger={isEvCharger}
                          />
                        )}
                      </>
                    )}
                </OfferSelect>
              </ConfirmOfferPageSection>
              <ConfirmOfferPageSection isNewHomes={isNewHomes}>
                {!isNewHomes && (
                  <>
                    <StepsTypographyMover isMobile={isMobile}>
                      <Typography
                        variant="p"
                        size={tokens.FONT_SIZE_4}
                        fontWeight="medium"
                      >
                        After getting started today, here are your steps to
                        clean energy:
                      </Typography>
                    </StepsTypographyMover>
                    <ConfirmOfferPageCheckoutStepsColumnLayout>
                      {OPTY_PROGRESS_STEPS.map((step) => (
                        <CheckoutStep key={step.description} step={step} />
                      ))}
                    </ConfirmOfferPageCheckoutStepsColumnLayout>
                  </>
                )}
                <CheckoutFooter
                  buttonText="Continue"
                  onClick={navigateToFirstCheckoutTask}
                  isDisabled={
                    isBuilderOfferSelected ||
                    (offer.isExpired ? !offer.isSigned : offer.isExpired)
                  }
                />
              </ConfirmOfferPageSection>
            </>
          )}
        </ConfirmOfferPageContentContainer>
      </CheckoutFullWidthContentGrid>
    </StyledCheckoutGrid>
  );
};

const StyledCheckoutGrid = styled(CheckoutGrid)<{
  showMobilePadding?: boolean;
}>`
  ${({ showMobilePadding }) =>
    showMobilePadding &&
    `
    padding: 72px 16px;
  `}
`;

const StyledMobileHeaderNote = styled.div`
  margin: 0px !important;
`;

const StyledNonSolarDisplay = styled(NonSolarDisplay)<{
  isMobile: boolean;
  isTablet: boolean;
  isNewHomes?: boolean;
}>`
  margin-top: 30px;
  height: ${({ isMobile }) => (isMobile ? "200px" : "375px")};
  ${({ isNewHomes }) =>
    isNewHomes &&
    `
    align-items: stretch;
    overflow: hidden;
  `}
  > img {
    ${({ isMobile, isNewHomes }) =>
      isNewHomes
        ? `
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    `
        : `
      height: ${isMobile ? "168px" : "280px"};
    `}
  }
`;

const ConfirmOfferPageContentContainer = styled.div<{ isMobile?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  margin-top: ${({ isMobile }) => (isMobile ? "0px" : "40px")};
  @media (max-width: ${maxWidth}px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ConfirmOfferPageSection = styled.div<{ isNewHomes?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: ${({ isNewHomes }) => (isNewHomes ? "flex-end" : "normal")};
  gap: 20px;
  @media (max-width: ${maxWidth}px) {
    width: 100%;
  }
`;
const OfferSelect = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;
const StepsTypographyMover = styled.div<{ isMobile?: boolean }>`
  width: 100%;
  margin-top: 50px;
  margin-bottom: 30px;
  @media (max-width: 1024px) {
    margin-top: ${({ isMobile }) => (isMobile ? "0px" : "30px")};
    margin-bottom: ${({ isMobile }) => (isMobile ? "24px" : "30px")};
  }
`;
const ConfirmOfferPageCheckoutStepsColumnLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  column-gap: 25px;
  row-gap: 20px;
  grid-template-rows: repeat(4, 1fr);
  grid-auto-flow: column;
  @media (max-width: 1024px) {
    grid-template-rows: repeat(8, 1fr);
  }
`;

const ConfirmOfferNewHomesDesignWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: 30px;
  height: 375px;
  border-radius: 10px;
  overflow: hidden;
`;

const ConfirmOfferNewHomesDesignImage = styled.div<{ bgImageUrl: string }>`
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: url(${({ bgImageUrl }) => bgImageUrl}) no-repeat center;
  background-size: cover;
`;

const NewHomesDisclaimer = styled(Typography)`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 16px;
  z-index: 1;
`;

export { CheckoutConfirmOfferPage };
